import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewContainerRef
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  EventService,
  OCC_USER_ID_ANONYMOUS,
  OCC_USER_ID_CURRENT,
  RoutingService,
  SemanticPathService,
  User,
  UserIdService,
  WindowRef,
  createFrom
} from "@spartacus/core";
import { Order, ReplenishmentOrder } from '@spartacus/order/root';
import { LaunchDialogService } from '@spartacus/storefront';
import { UserAccountFacade } from "@spartacus/user/account/root";
import { BehaviorSubject, Observable, Subscription, combineLatest, interval, of } from 'rxjs';
import { map, take } from "rxjs/operators";
import { CustomUserOrderAdapter } from 'src/app/spartacus/custom/core/user/connectors/order/custom-user-order.adapter';
import { CustomUserOrderService } from 'src/app/spartacus/custom/core/user/facade/order/custom-user-order.service';
import { TrustedShopsService } from "../../../../../../custom/services/trustedshops.service";
import { TagManagerFeatureService } from "../../../../../features/tracking/custom-events/tag-manager-feature.service";
import { CUSTOM_LAUNCH_CALLER } from '../../../../../spartacus-custom.module';
import { CustomCheckoutService } from '../../../../core/src/checkout/facade/custom-checkout.service';

declare global {
  interface Window {
    trustbadge: any;
  }
}

@Component({
  selector: 'cx-order-confirmation-overview',
  templateUrl: './custom-order-confirmation-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomOrderConfirmationOverviewComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription = new Subscription();
  private isLoading = new BehaviorSubject<boolean>(true);
  loading$ = this.isLoading.asObservable();
  isSequra: boolean;
  private order$: Observable<Order | ReplenishmentOrder | undefined>;
  order: Order | ReplenishmentOrder;
  displayOrderbyCode: boolean;
  placedOrder: void | Observable<ComponentRef<any>>;
  date: Date;
  location: Location;

  user$: Observable<User | undefined> = this.userAccount.get();
  professional = false;

  constructor(
    protected checkoutService: CustomCheckoutService,
    private activatedRoute: ActivatedRoute,
    private userOrderService: CustomUserOrderService,
    protected launchDialogService: LaunchDialogService,
    protected vcr: ViewContainerRef,
    private winRef: WindowRef,
    private trustedShopsService: TrustedShopsService,
    protected tagManagerFeatureService: TagManagerFeatureService,
    protected eventService: EventService,
    private userAccount: UserAccountFacade,
    @Inject(PLATFORM_ID) private platformId: any,
    protected customUserOrderAdapter: CustomUserOrderAdapter,
    protected routingService: RoutingService,
    protected userIdService?: UserIdService,
    protected semanticPathService?: SemanticPathService,
  ) {
    this.subscriptions.add(this.user$.subscribe(user => {
      this.professional = user?.professional || false;
    }));
    if (this.winRef.isBrowser()) {
      this.displayOrderbyCode = !!this.activatedRoute.snapshot.url[1]?.path;
      this.subscriptions.add(
        combineLatest([
          this.checkoutService.getPaypalOrderLoading(),
          this.checkoutService.getPaypalOrderSuccess(),
          this.checkoutService.getPaypalOrderError(),
          this.userOrderService.getOrderDetails(),
        ]).subscribe(([paypalOrderLoading, paypalsuccess, paypalerror, placeOrderLoading]) => {
          if ((paypalOrderLoading || Object.keys(placeOrderLoading).length === 0) && !!this.displayOrderbyCode) {
            this.launchDialogService.launch(
              CUSTOM_LAUNCH_CALLER.PAYPAL_ACTIONS_SPINNER,
              this.vcr,
            );
          }
          if (paypalerror || Object.keys(placeOrderLoading).length !== 0 || !this.displayOrderbyCode) {
            const spinner = this.winRef.document.getElementsByClassName('cx-dialog-popover-center-backdrop')[0];
            if (this.winRef.document.getElementsByClassName('cx-dialog-popover-center-backdrop').length) {
              spinner.remove();
            }
            this.launchDialogService.clear(
              CUSTOM_LAUNCH_CALLER.PAYPAL_ACTIONS_SPINNER,
            );
          }
        }));
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.location = this.winRef.nativeWindow.location;
      const paymentType = this.winRef.localStorage.getItem('paymentType');

      this.isSequra = paymentType === 'Sequra';

      this.order$ = this.displayOrderbyCode ?
        this.userOrderService.getOrderDetails() :
        this.checkoutService.getOrderDetails();
      this.subscriptions.add(
        combineLatest([this.userIdService.getUserId(), this.order$]).subscribe(([userId, order]) => {
          this.order = order;
          // Set order confirmation checked for user, in order to show order confirmation page only once
          const userOrderSelector = Object.create({
            [OCC_USER_ID_ANONYMOUS]: order.guid,
            [OCC_USER_ID_CURRENT]: order.code,
          });
          if (userOrderSelector[userId]) {
            this.subscriptions.add(
              this.customUserOrderAdapter.setOrderConfirmationChecked(userId, userOrderSelector[userId])
                .subscribe(order => {
                  if (Boolean((order as Order).confirmationChecked)) {
                    this.routingService.go({ cxRoute: 'home' });
                  } else {
                    this.checkoutService.loadOrderConfirmationSuccess(this.order)
                  }
                }));
          }
        }));

      let int = interval(500).pipe(map(x => x + 1), take(60)).subscribe(next => {
        if (Object.keys(this.order).length !== 0) {
          if (this.trustedShopsService.enabled) {
            if (this.winRef.nativeWindow.trustbadge !== undefined) {
              this.trustBadgeReInitialize();
              this.isLoading.next(false);
              int.unsubscribe();
            }
          } else {
            this.isLoading.next(false);
            int.unsubscribe();
          }
        }
      })
    }
  }

  ngOnDestroy(): void {
    if (this.winRef.isBrowser()) {
      this.winRef.localStorage.removeItem('paymentType');
      if (this.subscriptions) {
        this.subscriptions.unsubscribe();
      }
      if (this.displayOrderbyCode) {
        this.userOrderService.clearOrderDetails();
      } else {
        this.checkoutService.clearCheckoutData();
      }
    }
    this.trustBadgeReInitialize();
  }

  protected trustBadgeReInitialize() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.winRef.nativeWindow.trustbadge !== undefined) {
        if (this.winRef.nativeWindow.trustbadge.remove !== undefined) this.winRef.nativeWindow.trustbadge.remove();
        setTimeout(() => {
          if (this.winRef.nativeWindow.trustbadge.reInitialize !== undefined) this.winRef.nativeWindow.trustbadge.reInitialize()
        }, 500);
      }
    }
  }
}
